import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useCurrency } from "@/context/CurrencyContext";

const PRIORITY_LABEL = { e_ulet: "E ulët", e_mesme: "E mesme", e_larte: "E lartë", urgjente: "Urgjente" };
const PRIORITY_COLOR = {
  e_ulet: "bg-[#EAE4D9] text-[#5C5C5C]",
  e_mesme: "bg-[#EAE4D9] text-[#2C4C3B]",
  e_larte: "bg-[#D4A24C]/20 text-[#8B6914]",
  urgjente: "bg-[#9E3C3C]/15 text-[#9E3C3C]",
};
const STATUS_LABEL = { planifikuar: "Planifikuar", aprovuar: "Aprovuar", perfunduar: "Përfunduar", anuluar: "Anuluar" };

const ALL_SURFACES = ["M", "O", "D", "B", "L"];

export default function TreatmentPlan({ patientId }) {
  const { format, symbol } = useCurrency();
  const [list, setList] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    tooth_number: "", surfaces: [], procedure_name: "", procedure_id: "",
    estimated_cost: 0, priority: "e_mesme", notes: "",
  });

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get("/treatment-plans", { params: { patient_id: patientId } }),
      api.get("/procedures"),
    ]);
    setList(a.data); setProcedures(b.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/treatment-plans", { ...form, patient_id: patientId });
      toast.success("Plani u shtua");
      setOpen(false);
      setForm({ tooth_number: "", surfaces: [], procedure_name: "", procedure_id: "", estimated_cost: 0, priority: "e_mesme", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setStatus = async (item, newStatus) => {
    try {
      await api.put(`/treatment-plans/${item.id}`, { ...item, status: newStatus });
      if (newStatus === "perfunduar") toast.success("Procedura u shënua e përfunduar dhe u shtua në historik");
      else toast.success("Statusi u përditësua");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Fshini këtë procedurë nga plani?")) return;
    await api.delete(`/treatment-plans/${id}`);
    toast.success("U fshi");
    load();
  };

  const toggleSurf = (s) => {
    setForm((p) => ({ ...p, surfaces: p.surfaces.includes(s) ? p.surfaces.filter((x) => x !== s) : [...p.surfaces, s] }));
  };

  const totalEstimate = list
    .filter((p) => p.status !== "perfunduar" && p.status !== "anuluar")
    .reduce((s, p) => s + (p.estimated_cost || 0), 0);
  const completed = list.filter((p) => p.status === "perfunduar");
  const totalDone = completed.reduce((s, p) => s + (p.estimated_cost || 0), 0);

  return (
    <div className="space-y-6" data-testid="treatment-plan">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Plani Klinik</div>
          <h3 className="text-2xl font-semibold tracking-tight mt-1">Plani i Trajtimit</h3>
          <p className="mt-1 text-sm text-[#5C5C5C]">Procedurat e planifikuara dhe ato të kryera për pacientin.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary" data-testid="add-plan-button"><Plus size={16} /> Shto Procedurë</button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-xl">
            <DialogHeader><DialogTitle>Shto Procedurë në Plan</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div>
                <Label className="label-eyebrow">Procedura</Label>
                <Select value={form.procedure_id} onValueChange={(v) => {
                  const p = procedures.find((x) => x.id === v);
                  setForm({ ...form, procedure_id: v, procedure_name: p?.name || "", estimated_cost: p?.default_cost || 0 });
                }}>
                  <SelectTrigger className="mt-2" data-testid="plan-procedure-select"><SelectValue placeholder="Zgjidh nga katalogu" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {procedures.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name} · {format(p.default_cost)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-eyebrow">Dhëmbi (FDI)</Label>
                  <Input value={form.tooth_number} onChange={(e) => setForm({...form, tooth_number: e.target.value})}
                         placeholder="P.sh. 16" className="mt-2" data-testid="plan-tooth" />
                </div>
                <div>
                  <Label className="label-eyebrow">Kosto Vlerësim ({symbol})</Label>
                  <Input type="number" step="0.01" value={form.estimated_cost}
                         onChange={(e) => setForm({...form, estimated_cost: Number(e.target.value)})} className="mt-2" />
                </div>
              </div>
              <div>
                <Label className="label-eyebrow">Sipërfaqet</Label>
                <div className="mt-2 flex gap-2">
                  {ALL_SURFACES.map((s) => (
                    <button key={s} type="button" onClick={() => toggleSurf(s)}
                            className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                              form.surfaces.includes(s) ? "border-[#2C4C3B] bg-[#2C4C3B] text-[#FDFBF7]" : "border-[#E2DFD8] text-[#5C5C5C]"
                            }`}>{s}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="label-eyebrow">Prioriteti</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                  <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-eyebrow">Shënime</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="mt-2" />
              </div>
              <DialogFooter>
                <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Anulo</button>
                <button type="submit" className="btn-primary">Ruaj</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="surface-card p-5">
          <div className="label-eyebrow">Të Planifikuar</div>
          <div className="mt-1 text-xl font-semibold">{list.filter((p) => p.status === "planifikuar").length}</div>
        </div>
        <div className="surface-card p-5">
          <div className="label-eyebrow">Vlerësim Kosto</div>
          <div className="mt-1 text-xl font-semibold">{format(totalEstimate)}</div>
        </div>
        <div className="surface-card p-5">
          <div className="label-eyebrow">Përfunduar</div>
          <div className="mt-1 text-xl font-semibold">{completed.length} · {format(totalDone)}</div>
        </div>
      </div>

      <div className="surface-card overflow-hidden">
        {list.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#5C5C5C]">Pa procedura në plan. Klikoni "Shto Procedurë" për të filluar.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FDFBF7] text-left">
              <tr>
                <th className="px-6 py-3 label-eyebrow">Procedura</th>
                <th className="px-6 py-3 label-eyebrow">Dhëmbi</th>
                <th className="px-6 py-3 label-eyebrow">Sipërfaqet</th>
                <th className="px-6 py-3 label-eyebrow">Prioriteti</th>
                <th className="px-6 py-3 label-eyebrow">Statusi</th>
                <th className="px-6 py-3 label-eyebrow text-right">Kosto</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className="border-t border-[#E2DFD8]" data-testid={`plan-${p.id}`}>
                  <td className="px-6 py-3 font-medium">{p.procedure_name}</td>
                  <td className="px-6 py-3 font-mono">{p.tooth_number || "—"}</td>
                  <td className="px-6 py-3 font-mono text-xs">{p.surfaces?.join(", ") || "—"}</td>
                  <td className="px-6 py-3">
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${PRIORITY_COLOR[p.priority]}`}>
                      {PRIORITY_LABEL[p.priority]}
                    </span>
                  </td>
                  <td className="px-6 py-3">
                    <Select value={p.status} onValueChange={(v) => setStatus(p, v)}>
                      <SelectTrigger className="h-8 text-xs w-36" data-testid={`plan-status-${p.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-6 py-3 text-right font-mono">{format(p.estimated_cost)}</td>
                  <td className="px-6 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {p.status === "aprovuar" && (
                        <button onClick={() => setStatus(p, "perfunduar")} className="p-1.5 rounded hover:bg-[#4A8B71]/10 text-[#2C4C3B]" title="Shëno si Përfunduar">
                          <CheckCircle2 size={14} />
                        </button>
                      )}
                      <button onClick={() => remove(p.id)} className="p-1.5 rounded hover:bg-[#9E3C3C]/10 text-[#9E3C3C]" title="Fshi">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
